import {
  API_PROXY_CLIENT_LOGIN_ENDPOINT,
  API_PROXY_LOGOUT_ENDPOINT,
  API_PROXY_REFRESHTOKEN_ENDPOINT,
  API_TOKEN_ENDPOINT,
  API_VALIDATETOKEN_ENDPOINT,
} from "../../constants/endpoints";
import { idmsApi } from "./api";

const DEBUG_AUTH = process.env.REACT_APP_DEBUG_AUTH === "true";
const mask = (s?: string | null) =>
  s ? s.replace(/^(.{2}).*(.{2})$/, "$1***$2") : "";

const authApi = idmsApi.injectEndpoints({
  endpoints: (builder) => ({
    // Generic iframe logins still depend on cache-key/dealer-aware token requests.
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (request) => ({
        url: API_TOKEN_ENDPOINT,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        if (DEBUG_AUTH) {
          console.debug("[auth] login -> request", {
            usernameLen: arg?.username?.length,
            hasPassword: Boolean(arg?.password),
            productId: arg?.productId,
            clientId: arg?.clientId,
            clientSecret: mask(arg?.clientSecret as string | null | undefined),
          });
        }
        try {
          const { data } = await queryFulfilled;
          if (DEBUG_AUTH) {
            console.debug("[auth] login <- success", {
              accessTokenLen: data?.access_token?.length,
              refreshTokenLen: data?.refresh_token?.length,
            });
          }
        } catch (e) {
          if (DEBUG_AUTH) {
            console.debug("[auth] login <- error", e);
          }
        }
      },
      // providesTags: (result, error, arg) =>
      //   result
      //     ? [...result.map(({ id }) => ({ type: "Auth" as const, id })), "Auth"]
      //     : ["Auth"],
    }),

    proxyClientLogin: builder.mutation<LoginResponse, ProxyClientLoginRequest>({
      query: (request) => ({
        url: API_PROXY_CLIENT_LOGIN_ENDPOINT,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        if (DEBUG_AUTH) {
          console.debug("[auth] proxyClientLogin -> request", {
            usernameLen: arg?.username?.length,
            hasPassword: Boolean(arg?.password),
          });
        }
        try {
          const { data } = await queryFulfilled;
          if (DEBUG_AUTH) {
            console.debug("[auth] proxyClientLogin <- success", {
              accessTokenLen: data?.access_token?.length,
              refreshTokenLen: data?.refresh_token?.length,
            });
          }
        } catch (e) {
          if (DEBUG_AUTH) {
            console.debug("[auth] proxyClientLogin <- error", e);
          }
        }
      },
    }),

    // Cookie-based silent refresh for the standalone IDMSPortal. The refresh token is read
    // from the httpOnly cookie server-side, so no token/secret is sent from the browser.
    proxyRefreshToken: builder.mutation<LoginResponse, void>({
      query: () => ({
        url: API_PROXY_REFRESHTOKEN_ENDPOINT,
        method: "POST",
      }),
    }),

    // Revokes the refresh token and clears the httpOnly cookie.
    proxyLogout: builder.mutation<void, void>({
      query: () => ({
        url: API_PROXY_LOGOUT_ENDPOINT,
        method: "POST",
      }),
    }),

    validateToken: builder.query({
      query: () => API_VALIDATETOKEN_ENDPOINT,
    }),
  }),
});

export const {
  useLoginMutation,
  useProxyClientLoginMutation,
  useProxyRefreshTokenMutation,
  useProxyLogoutMutation,
  useValidateTokenQuery,
} = authApi;
